export const TEMPLATE_TYPES = {
  SOAP: "SOAP",
  CUSTOM: "CUSTOM",
};

export const TEMPLATE_FIELDS = {
  SUBJECTIVE: "SUBJECTIVE",
  OBJECTIVE: "OBJECTIVE",
  ASSESSMENT: "ASSESSMENT",
  PLAN: "PLAN",
  CUSTOM: "CUSTOM",
};

export const SOAP_FIELDS = [
  {
    fieldName: TEMPLATE_FIELDS.SUBJECTIVE,
    fieldIdx: 0,
    fieldValue: "",
  },
  {
    fieldName: TEMPLATE_FIELDS.OBJECTIVE,
    fieldIdx: 1,
    fieldValue: "",
  },
  {
    fieldName: TEMPLATE_FIELDS.ASSESSMENT,
    fieldIdx: 2,
    fieldValue: "",
  },
  { fieldName: TEMPLATE_FIELDS.PLAN, fieldIdx: 3, fieldValue: "" },
];

export const CUSTOM_FIELDS = [
  { fieldName: TEMPLATE_FIELDS.CUSTOM, fieldIdx: 0, fieldValue: "" },
];

export const DEFAULT_TEMPLATE_EXAMPLES = {
  [TEMPLATE_TYPES.SOAP]: {
    name: "Eg: Structured Diagnostic Discussion",
    description:
      "Eg: A structured SOAP template ready to accommodate your noteworthy notetaking style.",
  },
  [TEMPLATE_TYPES.CUSTOM]: {
    name: "Eg: Freeform Template",
    description:
      "A freeform template allows you to create a template with no predefined fields. You can add your own fields and customize the template to your needs.",
  },
};

export const FIELD_STYLES = {
  [TEMPLATE_FIELDS.SUBJECTIVE]: {
    container:
      "bg-gradient-to-b dark:from-blue-900/50 from-blue-100 to-transparent border border-blue-200 dark:border-blue-800/50",
    text: "text-blue-400 dark:text-blue-500",
    placeholder:
      "Enter subjective template.\n\nIf you're not sure what to include, try using the samples above.",
    simplePlaceholder: "Enter subjective template",
  },
  [TEMPLATE_FIELDS.OBJECTIVE]: {
    container:
      "bg-gradient-to-b dark:from-pink-900/50 from-pink-100 to-transparent border border-pink-200 dark:border-pink-800/50",
    text: "text-pink-400 dark:text-pink-500",
    placeholder:
      "Enter objective template.\n\nThese do best with vitals and body systems structured line-by-line with your default next to them",
    simplePlaceholder: "Enter objective template",
  },
  [TEMPLATE_FIELDS.ASSESSMENT]: {
    container:
      "bg-gradient-to-b dark:from-green-900/50 from-green-100 to-transparent border border-green-200 dark:border-green-800/50",
    text: "text-green-400 dark:text-green-500",
    placeholder:
      "Enter assessment template.\n\nPro Tip: These do best with simple headers (Overall: _____ ). That said, you can get wild and even ask for differentials here!",
    simplePlaceholder: "Enter assessment template",
  },
  [TEMPLATE_FIELDS.PLAN]: {
    container:
      "bg-gradient-to-b dark:from-yellow-900/50 from-yellow-100 to-transparent border border-yellow-200 dark:border-yellow-800/50",
    text: "text-yellow-400 dark:text-yellow-500",
    placeholder:
      "Enter plan template.\n\nPro Tip: If you want a very specific structure for discussions/costs/recommendations/treatments/diagnostics and more - this is your place to ask for it!",
    simplePlaceholder: "Enter plan template",
  },
  [TEMPLATE_FIELDS.CUSTOM]: {
    container: "bg-gray-100 dark:bg-gray-700",
    text: "text-gray-400",
    placeholder:
      "Enter template here, and format it to your heart's content. If you're not sure what to include, try using the samples above.",
    simplePlaceholder: "Enter template",
  },
};

// ==================== SOAP TEMPLATE EXAMPLES ====================

const DEFAULT_SOAP_TEMPLATE_EXAMPLE = {
  name: "Scribenote SOAP Default",
  [TEMPLATE_FIELDS.SUBJECTIVE]: "",
  [TEMPLATE_FIELDS.OBJECTIVE]: ` 
Temperature: normal
Heart Rate: within normal limits
Weight: list in kg
Respiratory Rate: within normal limits
Capillary Refill Time: moist, <2 seconds
Mucous Membranes: pink
Hydration Status: normal hydration
Body Condition Score: 5/9 
Pain Score: 0
Fear Anxiety Stress Score: Green
Eyes: no clouding, no debris, sclera is clear
Ears: no debris or erythema
Nasal: normal
Oral/Dental: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong femoral pulses
Respiratory: lung sounds are clear, no crackles or wheezes
Neurological: normal mentation
Musculoskeletal: good range of motion, no gait deficits, normal muscling 
Lymphatic: small, soft, pliable
Gastrointestinal: comfortable on palpation, no mass effect, no fluid wave
Reproductive/Urinary: normal conformation, no discharge
Integument: healthy skin and coat, no evidence of ectoparasites
Masses: no lumps or bumps
`,
  [TEMPLATE_FIELDS.ASSESSMENT]: "",
  [TEMPLATE_FIELDS.PLAN]: "",
  preferences: `Write the Subjective in paragraph format. Use Fahrenheit for temperature. List the size, location and description for all masses. Write the Assessment in bullet points (use dashes) without subheaders; be brief and concise. Write the plan in bullet points (use dashes) without subheaders; be specific. Include all potential courses of treatments discussed with costs, even if declined (note if declined). If vaccines are noted, note locations: e.g. Rabies RH SQ 3y, DHPP/Lepto LH SQ 3y/1y, Lepto LH SQ 1y (if alone), Bord orally. Use medical terminology where possible.`,
  sampleTranscript: `Daisy 4y/o mn sheepdog cross. No health concerns; no vomiting, diarrhea, coughing, or sneezing.
Exam: Normal except for moderate tartar buildup on the premolars and body condition score of 8/9
Vaccines given: Rabies and DHPP/Lepto.
Owners declined Bordetella vaccine.
Discharged with 3 months of Simparica Trio.
Advised regular dental care via teeth brushing or CET dental chews for tartar control.`,
};

const ADULT_WELLNESS_SOAP_TEMPLATE_EXAMPLE = {
  name: "Adult Wellness - Vaccine",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Pet information:
Any C/S/V/D?:
Any health concerns:
Diet:
Flea/tick/heartworm medication:
Other medications:
Previous vaccine reactions?:

[answer the question without restarting the question - ie, instead of "Any C/S/V/D?: No", write "No C/S/V/D" for that line.]
`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `General Appearance: BAR
Fear Anxiety Stress Score: Green
Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5/9
Eyes: clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: Regular rhythm; no murmur detected, strong pulses.
Respiratory: Lungs auscultate clear bilaterally. 
Abdomen: Abdomen palpates normally
Musculoskeletal: Normal gait
Integument: healthy skin and coat
Lymph nodes: soft, small, pliable
Urogenital: normal on exam
Neurologic: Normal mentation`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Overall:`,
  [TEMPLATE_FIELDS.PLAN]: `Vaccines: 

Prevention sent home: 

Follow up:`,
  preferences: `Write in point form. 
For dog vaccines if given denote Rabies RH SQ 3y, DHPP/Lepto combo LH SQ 3y/1y 
Oral Bordetella
For cat vaccines if given denote Rabies RH SQ 3y, FVRCP LH SQ 3y, FLV LF SQ 1y
`,
  sampleTranscript: `Dobby 4y/o mn sheepdog cross. No health concerns. No V/D/C/S. All Exam normal except tartar on PM4's. Gave Rabies, DHPP/Lepto. Owners declined Bordetella vaccine
Send home 3 months of Simparica Trio. Recommend Teeth brushing or using CET dental chews for dental tartar.`,
};

const MY_LUMPS_AND_BUMPS_SOAP_TEMPLATE_EXAMPLE = {
  name: "Lumps and Bumps",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `History:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
General Appearance: Bright, alert and responsive
Hydration: Hydration appears normal
Eyes: no abnormalities
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: Regular rhythm; no murmur detected
Respiratory: Lungs auscultate clear bilaterally. 
Abdomen: Abdomen palpates normally; no pain, tenderness or internal masses on palpation
Musculoskeletal: Normal ambulation, Normal ROM
Integument: skin looks normal; hair coat in good condition. No ectoparasites. No masses found.
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation
Masses: List any masses with locations and size descriptions here`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem List:
Differentials:`,
  [TEMPLATE_FIELDS.PLAN]: `Diagnostics performed: 

Plan/ Follow up:`,
  preferences: `Bring abnormal findings to the top of the objective section.`,
  sampleTranscript: `Shelley is a 9 y/o female spayed Weimaraner. Presented for assessment and update of all masses present and measurements. Shelly has moderate age related changes in both eyes. There is moderate dental disease present with moderate tartar and gingivitis present. Lymph nodes are all small and soft. There are multiple masses. Five masses that we'll measure today. The first is on the right scapula. It is raised and round, measuring 2.3 by 3.5 cm. It is dermal and firm. There is a large mass on the left lateral abdomen that measures 5cm by 5cm. It is soft and subcutaneous. There is a mass located on the caudal aspect of the left hind limb at the distal aspect of the limb. It measures 1 x 1 cm. There is a firm mass on the occipital crest of the head. It measures 0.5 by 0.7 cm. And the last mass is on the right front foot on the dorsal aspect. It is a small skin tag that does not have a deep attachment and is 1cm long. All these masses can continue to be monitored. If any grow, change rapidly or become bothersome, they can be removed under general sedation.
Owner declines any FNA samples and wishes to measure and monitor masses at this time
  `,
};

const MY_GENERAL_CONSULT_SOAP_TEMPLATE_EXAMPLE = {
  name: "General Consult",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Pet's signalment: (Name, Age, breed, sex, if spayed or neutered)
History: 
Diet: 
HW/ flea + tick preventative:
Current Medications/ supplements:
Any vaccine or medication reactions:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
FAS: Green
Hydration: Hydration appears normal
Eyes: no clouding, no debris, sclera is clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong pulses.
Respiratory: Lungs sounds are clear, no crackles or wheezes
Abdomen: comfortable on palpation, no mass effect, no fluid wave
Musculoskeletal: Normal ambulation, Normal ROM
Integument: skin looks normal; hair coat in good condition. No ectoparasites. No masses found.
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem List:
Differentials: If no differentials note stable on exam`,
  [TEMPLATE_FIELDS.PLAN]: `Treatments: 
Diagnostics Completed: 
Prescriptions:  
Follow up:`,
  preferences: `Write in medical terminology, write in point form`,
  sampleTranscript: `Scribbles is a 15 year old FS DLH presenting for a senior wellness exam. Owner has concerns about difficulty jumping and vocalization at night. Lenticular sclerosis with moderate cataracts OU. Marked dental disease with halitosis. Vocalization may be due to cognitive changes, recommend giving gabapentin 100mg at bed time to help with wandering. Follow up with weigh in in 1 month, consider Senior bloodwork if continued muscle wasting or weight loss noted. 
  `,
};

const MY_RECHECK_SOAP_TEMPLATE_EXAMPLE = {
  name: "Recheck",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `History: 
When last examined: 
Any noted changes since:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `List the body system and findings that are discussed only. List body systems on separate lines.`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `General:`,
  [TEMPLATE_FIELDS.PLAN]: `Next Steps:`,
  preferences: `Use dashes. Be concise`,
  sampleTranscript: `Gizmo presented for recheck from 2 weeks ago. Has been doing very well on Fibre diet and lactulose was discontinued 1 week ago. Normal bowel movements daily. Hydration is normal. Abdominal palpation reveals soft and comfortable abdomen with no firm stool palpated. Overall improvement is noted. Recommend to continue Royal Canin Fibre diet dry and continue to monitor bowel movements in litter box.`,
};

const ALLERGY_APPOINTMENT_SOAP_TEMPLATE_EXAMPLE = {
  name: "Allergy",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Where is the patient scratching:
Is it all the time or seasonal:
When did that start:
Diet and treats: 
Topicals used:
Medications: 
Other noted historical concerns:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
FAS: Green
Hydration: Hydration appears normal
Eyes: no clouding, no debris, sclera is clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong pulses.
Respiratory: Lungs sounds are clear, no crackles or wheezes
Abdomen: comfortable on palpation, no mass effect, no fluid wave
Musculoskeletal: Normal ambulation, Normal ROM
Integument: No erythema or lichenification present, no ectoparasites
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem list: 
Differential diagnosis:`,
  [TEMPLATE_FIELDS.PLAN]: `Cytology samples taken today: (yes/ no) and location
Topicals recommended:
Diet recommended:

Other follow up:`,
  preferences: `Be detailed when discussing subjective information and plan recommendations.`,
  sampleTranscript: `Cali is a 10 year old female spayed golden retriever. She has had allergies her whole life. They are all year long. She has had many ear infections and skin infections. She is currently on Apoquel and every other day ketoconazole. Her owner bathes her twice a week with prohex shampoo. Feet have been extremely raw and sore for 2 weeks. She has been licking alot. Front right foot has erythema and moist dermatitis interdigitally. Remainder of feet have moderate erythema on the ventral aspect. Cytology samples of the feet taken, Continue with prohex shampoo but increase frequency to daily for 5 minutes until improvement is noted. Recommend 12 week strict diet trial with Hypo HP diet (no treats or other snacks).`,
};

// ==================== END SOAP TEMPLATE EXAMPLES ====================

// ==================== FREEFORM TEMPLATE EXAMPLES ====================
const MY_ER_ROUNDS_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "ER Rounds",
  [TEMPLATE_FIELDS.CUSTOM]: `Patient name/signalment:
History:
Presenting complaint:
Completed diagnostics:
Pending Diagnostics:
Results:
Client expectations:
Plan:
…
Patient name/signalment:
History:
Presenting complaint:
Completed diagnostics:
Pending Diagnostics:
Results:
Client expectations:
Plan:`,
  preferences: `Fill in the rounds summary per patient discussed. Write in medical terminology, write in point form`,
  sampleTranscript: `Julius: 3 y/o MN DSH
Straining to urinate, vocalizing when using litter box. One episode of urinating blood outside the litter box. Completed radiographs, ultrasound, urinalysis no stones noted. Only red blood cells present on urinalysis. Plan IV fluids to flush bladder, Give Metacam SQ pending NSAID panel, Midazolam 0.2mg/kg IV q6hours for relaxation of sphincter

Scribbles: 7y/o MN DSH
Straining to defecate, markedly overweight, unable to groom himself. On lateral radiograph marked obstipation, cbc/chem10 all wnl. Owners cost concerned, have ok’d fluids- running those at 2x maintenance and enema’s (50/50 lube/water)  but no manual deobstipation

Dobby: 4 y/o MN Sheepdog mix 
Stick foreign body in L eye. Able to sedate and flush out. Marked corneal ulcer. Proceeding with temporary tarsorrhaphy under sedation and propofol later today. Bloodwork all normal.`,
};

const MY_CASE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Case Summary",
  [TEMPLATE_FIELDS.CUSTOM]: `Patient information:
History:
Summary of completed diagnostic findings:
Client expectations: 
Plan:`,
  preferences: `Write in medical terminology, write in point form`,
  sampleTranscript: `Winnie a 6yo intact female yorkie presented for vomiting and not eating for 3 days.
Radiographs revealed large closed pyometra, CBC/ Chem 10 revealed marked neutrophilia with left shift, mild evidence of dehydration with hemoconcentration
Client has consented to surgery, understands that patient is not out of the woods, recommend hospitalization overnight with continued fluid therapy, pain control. Consents to $6000 estimate maximum.`,
};

const MY_STRUCTURED_CALLBACK_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Structured Callback",
  [TEMPLATE_FIELDS.CUSTOM]: `Discussion:
Results:
Plan:`,
  preferences: `Write in point form. Note if anything is declined by the owner. `,
  sampleTranscript: `Calling to discuss bloodwork with Otto's owner. Otto has been drinking and peeing more. Owner notes he is more hungry as well and has been losing weight. RBC, WBC platelets all normal. Blood glucose, kidney values, electrolytes and proteins are normal. ALT is 200 (RR 50-150), ALP is 2000 (RR 65- 200). Marked elevation in ALP could be due to Cushing's disease (hyperadrenocorticism). Recommend urinalysis to assess urine specific gravity, recommend low dose dexamethasone test in clinic to determine if Pituitary or Adrenal type. Can consider ultrasound as additional imaging but is more costly. Owner does not wish to pursue ultrasound.`,
};

const MY_STRUCTURED_SURGERY_OVH_FELINE_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Structured Surgery - OVH Feline",
  [TEMPLATE_FIELDS.CUSTOM]: `Pre surg exam and bloodwork acceptable.
Preanesthetic Medications used:

Surgical notes:
The anesthetized animal was placed in dorsal recumbency and the area from just below the xiphoid to pubis was clipped and surgically prepared. The patient was surgically draped. A midline incision was made using a _____ blade through the skin and subcutaneous tissue.  The linea alba was located and gently elevated using forceps, and a stab incision was made into the linea alba using a ______ blade.  The incision into the linea was then extended cranially and caudal to the stab incision using metzenbaum scissors.

The uterus and ovaries were located and exteriorized. The suspensory ligaments on the right and left ovary were cut once fanned away from the pedicle. The ovarian pedicle was located and a window was made into the broad ligament right next to the vessels using a carmalt. A figure of 8 knot (pedicle tie) was used to provide hemostasis  to the pedicle. The knot was slid distally down to tighten it. The ovary was then freed from the pedicle by cutting above the knot and below the ovary using a scalpel blade. This was done to both the right and left pedicles. Both ovaries were ensured to be intact and hemostasis was confirmed.  Following the removal of the ovaries, the broad ligament was torn gently along the uterine body being careful of the uterine vessels.  The uterine body/cervix was clamped using carmalts. A millers knot, using ______ monocryl, was placed on the first crush when the first distal carmalt was removed.  A millers ligature using ______monocryl was used in the second crush from the second most proximal clamp.  The uterus was then removed by cutting above the ligature and below the clamp.  Once hemostasis was confirmed for all three sights, the abdomen was then closed.  The linea was closed using a simple continuous pattern of_____monocryl. The skin was closed using _______ monocryl suture in a intradermal pattern.

Recovery notes:
Recovery - E. tube pulled following return of swallowing reflex;  Recovery from anesthesia uneventful.

Medications to go home:
`,
  preferences: `Fill in the blanks`,
  sampleTranscript: `
User Dictates:
Used 5micrograms/kg dexmedetomidine and 0.1mg/kg hydromorphone IM - excellent quality sedation: 
“I made an incision with a 10 blade and used 2-0 monocryl on the uterus. Then I used 3-0 monocryl on the linea and the skin.” 
Sending home: 5 days of oral Metacam (start tomorrow), 0.05mg/kg
  `,
};

// ==================== END FREEFORM TEMPLATE EXAMPLES ====================

// ==================== TEMPLATE EXAMPLE MAPPINGS ====================

export const PREFERENCES_PREFILLS = {
  "Capitalize Abnormals": {
    description: "Write abnormals in all capital letters.",
  },
  "Use Point Form": {
    description: "Write in point form.",
  },
  "Medical Terminology": {
    description: "Use medical terminology where possible.",
  },
  "Use °F for Temperature": {
    description: "Use Fahrenheit for temperature.",
  },
  "Structure Recommendations": {
    description:
      "Include all potential courses of treatments discussed with costs, even if declined (note if declined).",
  },
};

export const SOAP_TEMPLATE_EXAMPLES = {
  [`${DEFAULT_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    DEFAULT_SOAP_TEMPLATE_EXAMPLE,
  [`${ADULT_WELLNESS_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    ADULT_WELLNESS_SOAP_TEMPLATE_EXAMPLE,
  [`${MY_LUMPS_AND_BUMPS_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    MY_LUMPS_AND_BUMPS_SOAP_TEMPLATE_EXAMPLE,
  [`${MY_GENERAL_CONSULT_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    MY_GENERAL_CONSULT_SOAP_TEMPLATE_EXAMPLE,
  [`${MY_RECHECK_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    MY_RECHECK_SOAP_TEMPLATE_EXAMPLE,
  [`${ALLERGY_APPOINTMENT_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    ALLERGY_APPOINTMENT_SOAP_TEMPLATE_EXAMPLE,
};

export const FREEFORM_TEMPLATE_EXAMPLES = {
  [`${MY_ER_ROUNDS_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_ER_ROUNDS_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_CASE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_CASE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_STRUCTURED_CALLBACK_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_STRUCTURED_CALLBACK_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_STRUCTURED_SURGERY_OVH_FELINE_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_STRUCTURED_SURGERY_OVH_FELINE_FREEFORM_TEMPLATE_EXAMPLE,
};

export const TEMPLATE_OPTIONS_MAP = {
  [TEMPLATE_TYPES.SOAP]: Object.values(SOAP_TEMPLATE_EXAMPLES).map(
    (template) => template.name,
  ),
  [TEMPLATE_TYPES.CUSTOM]: Object.values(
    FREEFORM_TEMPLATE_EXAMPLES,
  ).map((template) => template.name),
};

// ==================== TEMPLATE EXAMPLE MAPPINGS ====================

// ==================== TRANSCRIPT SAMPLE MAPPINGS ====================

const defaultTranscriptSample = {
  name: "Default",
  sampleTranscript: `Julius:
Eating well (on c/d diet)
Normal litter box habits

Mild ear debris, no otitis
Clear eyes, healthy teeth
Overweight (Body condition 7/9)
Normal lymph nodes
Normal heart rate
Comfortable abdomen
Normal mobility

Healthy cat with minor weight management needs.

Reduce c/d diet portion size due to high caloric content
Continue dental care attempts despite difficulty

Three-year rabies vaccine (right hind leg, subcutaneous)
Three-year feline viral vaccine (left hind leg, subcutaneous)
Nail trim
Lickable treat provided`,
};

export const TRANSCRIPT_SAMPLE_MAPPINGS = {
  DEFAULT: { ...defaultTranscriptSample },
  [TEMPLATE_TYPES.SOAP]: Object.values(SOAP_TEMPLATE_EXAMPLES).map(
    (template) => ({
      name: template.name,
      sampleTranscript: template.sampleTranscript,
    }),
  ),
  [TEMPLATE_TYPES.CUSTOM]: Object.values(
    FREEFORM_TEMPLATE_EXAMPLES,
  ).map((template) => ({
    name: template.name,
    sampleTranscript: template.sampleTranscript,
  })),
};
